import { ApplicationLibraryStatus } from '../../../graphql/schema/mutations/update-application-library-status'
import { GradeValue } from '../../../graphql/schema/types/grade-value'
import { BugTracker } from '../../../components/bug-tracker'
import {
  AccessComplexity,
  AccessVector,
  AuthenticationRequired,
  AvailabilityImpact,
  ConfidentialityImpact,
  IntegrityImpact
} from '../../../components/shared'
import {
  Library,
  LibraryClassUsageCount,
  RemediationGuidance
} from './ui-interfaces'

export enum LibraryFilterType {
  Grades = 'GRADES',
  Languages = 'LANGUAGES',
  Tags = 'TAGS',
  Servers = 'SERVERS',
  Status = 'STATUS',
  Usage = 'USAGE',
  Apps = 'APPS',
  Licenses = 'LICENSES',
  Environments = 'ENVIRONMENTS',
  Severity = 'SEVERITY',
  Severities = 'SEVERITIES',
  Vulnerable = 'VULNERABLE',
  QuickFilter = 'QUICKFILTER',
  Projects = 'PROJECTS',
  Repos = 'REPOS'
}

export enum SeverityName {
  CRITICAL = 'CRITICAL',
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
  NOTE = 'NOTE',
  OTHER = 'OTHER',
  NONE = 'NONE'
}

export interface CliLibraryFilter {
  id: string
  label: string
  count: number
}

export interface LibraryVulnerability {
  name: string
  description: string
  authentication: AuthenticationRequired
  references: any[]
  visible: boolean
  access_vector: AccessVector
  access_complexity: AccessComplexity
  availability_impact: AvailabilityImpact
  confidentiality_impact: ConfidentialityImpact
  integrity_impact: IntegrityImpact
  severity_value: number
  severity_code: string
  cvss_3_vector: string
  cvss_3_severity_value: number
  cvss_3_severity_code: string
  has_cvss3_score: boolean
  has_cve: boolean
  epss_score?: number
  epss_percentile?: number
  cisa?: boolean
}

interface LibraryApplication {
  primary: boolean
  master: boolean
  child: boolean
  importance?: any
  app_id: string
  name: string
  parent_app_id?: any
  total_modules: number
  language: string
  context_path: string
  last_seen: number
  license_level: string
  importance_description?: any
  first_seen: number
  app_library_status: ApplicationLibraryStatus
}

interface LibraryMergedApplication {
  primary: boolean
  master: boolean
  child: boolean
  importance?: any
  app_id: string
  name: string
  parent_app_id?: any
  total_modules: number
  language: string
  context_path: string
  last_seen: number
  license_level: string
  importance_description?: any
  first_seen: number
  app_library_status: ApplicationLibraryStatus
}

interface LibraryAssessServer {
  name: string
  hostname: string
  serverpath: string
  environment: string
  enabled: boolean
  server_id: number
  agent_version: string
}
export interface LibraryAssessGrid {
  id: string
  app_language: string
  hash: string
  file_name: string
  score: number
  grade: GradeValue
  tags: string[]
  servers: LibraryAssessServer[]
  bugTrackers: BugTracker[]
  classes_used: number
  class_count: number
  file_version: string
  latest_version: string
  latest_release_date: number
  licenses: string[]
  ossEnabled: boolean
  release_date: number
  vulns: LibraryVulnerability[]
  total_vulnerabilities: number
  apps: LibraryApplication[]
  remediationGuidance?: RemediationGuidance
  bugtracker_tickets: BugTracker[]
  mergedApps: LibraryMergedApplication[]
  library_class_usage_counts: LibraryClassUsageCount[]
  restricted: boolean
  invalid_version: boolean
  version_restriction?: number
  licenseViolation?: boolean
}

/** Interface shared across the UI components for both static and runtime libraries */
export type LibraryCommonUIProps = Pick<
  Library,
  | 'id'
  | 'name'
  | 'hash'
  | 'ossEnabled'
  | 'versions'
  | 'policies'
  | 'licenses'
  | 'group'
  | 'tags'
  | 'bugTrackers'
> & {
  language: Library['language'] | LibrarySCA['libraryLanguage']
  usage: {
    totalUsed: number
  }
}

export interface LibrarySCA
  extends Pick<Library, 'hash' | 'licenses' | 'privateLibrary' | 'custom'> {
  groupName: string
  artifactName: string
  version: string
  uuid: string
  fileName?: any
  projects: string[]
  libraryLanguage: string
  vulnerable: boolean
  privateLibrary: boolean
  severity: string
  releaseDate: string
  latestVersionReleaseDate: string
  latestVersion: string
  versionsBehind: number
  vulnerabilities: VulnerabilityCLI[]
  remediationAdvice: RemediationAdviceSCA
  absoluteParent?: AbsoluteParentSCA
}

interface AbsoluteParentSCA {
  group: string
  name: string
  version: string
}

interface RemediationAdviceSCA {
  closestStableVersion: string
  latestStableVersion: string
}

export interface VulnerabilityCLI {
  name: string
  description: string
  cvss2Vector?: string
  severityValue: number
  severity: string
  cvss3Vector?: string
  cvss3SeverityValue?: number
  cvss3Severity?: string
  hasCvss3: boolean
}
